// Upload utilities: Prefer Cloudflare R2 Worker when configured; otherwise Cloudinary; otherwise Firebase Storage.

import { storage, app } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

const auth = getAuth(app);

function getCloudinaryConfig() {
  // Priority: env vars -> localStorage overrides -> safe defaults
  const envName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const envPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  let cloudName = envName || (typeof window !== 'undefined' && window.localStorage && localStorage.getItem('CLD_CLOUD_NAME')) || '';
  let uploadPreset = envPreset || (typeof window !== 'undefined' && window.localStorage && localStorage.getItem('CLD_UPLOAD_PRESET')) || '';
  return { cloudName, uploadPreset };
}

export const isCloudinaryEnabled = () => {
  // Cloudinary is opt-in: only enabled if explicitly configured.
  const { cloudName, uploadPreset } = getCloudinaryConfig();
  return !!(cloudName && uploadPreset);
};

function uploadToCloudinary(file, user, onProgress) {
  const { cloudName, uploadPreset: preset } = getCloudinaryConfig();
  const folder = `avatars/${user.uid}`;
  const endpoint = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', preset);
  formData.append('folder', folder);
  formData.append('public_id', 'avatar');
  formData.append('overwrite', 'true');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint);
    xhr.onload = () => {
      try {
        const res = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300 && res && res.secure_url) {
          resolve(res.secure_url);
        } else {
          const msg = res?.error?.message || `Cloudinary upload failed (${xhr.status})`;
          reject(new Error(msg));
        }
      } catch (e) {
        reject(e);
      }
    };
    xhr.onerror = () => reject(new Error('Network error uploading to Cloudinary'));
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }
    xhr.send(formData);
  });
}

function uploadToFirebase(file, user, onProgress) {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `avatars/${user.uid}/avatar.${ext}`;
  const r = ref(storage, path);
  const task = uploadBytesResumable(r, file, { contentType: file.type || 'image/jpeg', cacheControl: 'no-cache, max-age=0' });
  return new Promise((resolve, reject) => {
    let stalledTimer = setTimeout(() => { try { task.cancel(); } catch {} }, 60000);
    task.on('state_changed', (snap) => {
      if (snap.totalBytes && typeof onProgress === 'function') {
        onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
      }
      if (stalledTimer) { clearTimeout(stalledTimer); }
      stalledTimer = setTimeout(() => { try { task.cancel(); } catch {} }, 60000);
    }, (err) => {
      if (stalledTimer) { clearTimeout(stalledTimer); stalledTimer = null; }
      reject(err);
    }, async () => {
      try {
        if (stalledTimer) { clearTimeout(stalledTimer); stalledTimer = null; }
        const url = await getDownloadURL(r);
        resolve(url);
      } catch (e) {
        reject(e);
      }
    });
  });
}

async function uploadToAvatarWorker(file) {
  const workerBase = (process.env.REACT_APP_AVATAR_R2_WORKER_URL || '').trim();
  if (!workerBase) {
    throw new Error('Missing REACT_APP_AVATAR_R2_WORKER_URL');
  }

  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to upload an avatar.');

  const token = await user.getIdToken(true);
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${workerBase.replace(/\/$/, '')}/api/avatar`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok || !data?.url) {
    throw new Error(data?.error || 'Avatar upload failed.');
  }

  return data.url;
}

export async function mirrorAvatarFromUrl(sourceUrl) {
  const workerBase = (process.env.REACT_APP_AVATAR_R2_WORKER_URL || '').trim();
  if (!workerBase) {
    throw new Error(
      'Avatar mirror is not configured. Set REACT_APP_AVATAR_R2_WORKER_URL to your Cloudflare Worker URL.'
    );
  }

  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to update an avatar.');

  const token = await user.getIdToken(true);
  const res = await fetch(`${workerBase.replace(/\/$/, '')}/api/avatar/mirror`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sourceUrl }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok || !data?.url) {
    throw new Error(data?.error || 'Avatar mirror failed.');
  }

  return data.url;
}

// Public API
export async function uploadAvatar(file, user, onProgress) {
  const workerBase = (process.env.REACT_APP_AVATAR_R2_WORKER_URL || '').trim();
  if (workerBase) {
    if (typeof onProgress === 'function') onProgress(5);
    const url = await uploadToAvatarWorker(file);
    if (typeof onProgress === 'function') onProgress(100);
    return url;
  }

  if (isCloudinaryEnabled()) {
    return await uploadToCloudinary(file, user, onProgress);
  }

  return await uploadToFirebase(file, user, onProgress);
}

/**
 * Uploads a profile photo to the configured backend.
 *
 * Current default: Cloudflare Worker + R2.
 * Configure with: REACT_APP_AVATAR_R2_WORKER_URL
 */
export async function uploadProfilePhoto(file) {
  if (!(file instanceof File)) throw new Error('Invalid file.');
  return await uploadToAvatarWorker(file);
}

// Fallback: create a small, optimized data URL client-side to avoid external uploads.
export async function imageFileToDataUrl(file, maxSize = 256, type = 'image/webp', quality = 0.82) {
  // Read via Object URL for performance
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load image'));
      image.src = objectUrl;
    });
    const { width, height } = img;
    const scale = Math.min(1, maxSize / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, targetW, targetH);
    const dataUrl = canvas.toDataURL(type, quality);
    return dataUrl;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
